import { z } from 'zod';

/**
 * Telefonía (docs/07).
 *
 * El guardia nunca escribe ni ve un número: oprime "llamar al residente" y el
 * servidor resuelve a dónde marcar. La excepción es esta prueba de banco, que
 * sí acepta un número escrito a mano para validar el hardware antes de que
 * exista el padrón telefónico.
 */

/**
 * Número marcable en la forma en que se le entrega a Asterisk: solo dígitos.
 *
 * Se normaliza aquí y no en el dialplan porque el dialplan es donde menos se
 * quiere lógica: cada regla ahí es una que solo se puede depurar con una
 * llamada real.
 */
export function normalizarNumero(numero: string): string {
  return numero.replace(/\D/g, '');
}

export const marcarPruebaSchema = z.object({
  numero: z
    .string()
    .trim()
    .min(3, 'Escribe un número')
    .max(20)
    .transform(normalizarNumero)
    .refine((n) => n.length >= 3, 'El número no tiene dígitos suficientes'),
  /**
   * Lo que aparece en la pantalla del teléfono de la caseta. En producción lo
   * arma el servidor con la unidad y el nombre del residente —"Casa 24 — Sra.
   * Ramírez"— y jamás con el número.
   */
  etiqueta: z.string().trim().max(40).optional(),
});
export type MarcarPrueba = z.infer<typeof marcarPruebaSchema>;

/**
 * Lo que el guardia manda para llamar: **una visita, nunca un número.**
 *
 * Es la diferencia de fondo entre esto y `marcarPrueba`, y la razón de que todo
 * el módulo de telefonía exista (docs/07). En un condominio normal el guardia
 * tiene la lista de teléfonos y marca desde su celular, así que se lleva los
 * números de todos los residentes el día que renuncia. Aquí Kodica resuelve a
 * quién llamar y el guardia no llega a verlo.
 */
export const llamarViviendaSchema = z.object({
  accesoId: z.string().min(1),
  /**
   * Cuál de los teléfonos de la casa, por su lugar en la cascada. Se omite para
   * el primero.
   *
   * Se elige por posición y no por número porque el guardia elige de una lista
   * de etiquetas —«Casa», «Cel. mamá»— y el número no viaja al navegador en
   * ningún momento.
   */
  orden: z.number().int().min(0).max(20).optional(),
});
export type LlamarVivienda = z.infer<typeof llamarViviendaSchema>;

export const ESTADOS_LLAMADA = [
  'SOLICITADA',
  'TIMBRANDO',
  'CONTESTADA',
  'FINALIZADA',
  'NO_CONTESTADA',
  'OCUPADO',
  'FALLIDA',
] as const;
export type EstadoLlamada = (typeof ESTADOS_LLAMADA)[number];

/**
 * Diagnóstico de la telefonía, pieza por pieza.
 *
 * Tres booleanos y no uno. Que Asterisk responda no significa que se pueda
 * llamar: arranca igual con cero aparatos registrados. Resumirlo en un solo
 * "OK" hace creer que el sistema está listo cuando el teléfono ni siquiera
 * está conectado.
 */
export interface EstadoTelefonia {
  /** Hay conmutador configurado en este servidor. */
  configurado: boolean;
  /** Asterisk responde a ARI. */
  conmutador: boolean;
  /** El teléfono de la caseta está registrado y responde. */
  telefono: boolean;
  /** El gateway de la línea está registrado y responde. */
  linea: boolean;
  version?: string;
}

/** Solo se puede marcar si las tres piezas están arriba. */
export function puedeLlamar(e: EstadoTelefonia | null | undefined): boolean {
  return !!e && e.conmutador && e.telefono && e.linea;
}

/** Lo que el navegador necesita para pintar el panel de llamada. */
export interface LlamadaEnCurso {
  id: string;
  estado: EstadoLlamada;
  /** A quién se llama, nunca el número. */
  etiqueta: string;
  solicitadaEn: string;
  contestadaEn: string | null;
  finalizadaEn: string | null;
  duracionS: number | null;
  motivoFin: string | null;
  hayGrabacion: boolean;
}

/** Estados en los que la llamada ya terminó y la pantalla deja de sondear. */
export function llamadaTerminada(estado: EstadoLlamada): boolean {
  return ['FINALIZADA', 'NO_CONTESTADA', 'OCUPADO', 'FALLIDA'].includes(estado);
}
